import { Chart } from 'chart.js';

const PALETTE = [
  '#2563eb', '#7c3aed', '#0ea5e9', '#10b981', '#f59e0b',
  '#ec4899', '#14b8a6', '#f43f5e', '#8b5cf6', '#06b6d4',
  '#84cc16', '#e11d48', '#6366f1', '#d97706', '#0d9488',
];

const fmtEuro = (cents) =>
  '\u20AC ' + (cents / 100).toLocaleString('nl-BE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export function budgetStatsCharts(config) {
  return {
    charts: {},
    _observer: null,

    init() {
      this.$nextTick(() => this.renderAll());
      this._observer = new MutationObserver(() => this.retheme());
      this._observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      Object.values(this.charts).forEach((c) => c?.destroy());
    },

    theme() {
      const dark = document.documentElement.classList.contains('dark');
      return {
        text: dark ? '#9ca3af' : '#6b7280',
        grid: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        tooltipBg: dark ? '#1f2937' : '#111827',
      };
    },

    retheme() {
      Object.values(this.charts).forEach((c) => c?.destroy());
      this.charts = {};
      this.renderAll();
    },

    renderAll() {
      const t = this.theme();
      this.renderDoughnut('categorie-chart', config.byCategorie, t);
      this.renderDoughnut('leverancier-chart', config.byLeverancier, t);
      this.renderBetaald('betaald-chart', config.betaald, t);
      this.renderMaand('maand-chart', config.perMaand, t);
      if (config.perRenner?.length) {
        this.renderBar('renner-chart', config.perRenner, t);
      }
    },

    renderDoughnut(canvasId, data, t) {
      const el = document.getElementById(canvasId);
      if (!el || !data?.length) return;
      const labels = data.map((d) => d.label);
      const values = data.map((d) => parseInt(d.total));
      this.charts[canvasId] = new Chart(el, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: PALETTE.slice(0, labels.length),
            borderWidth: 0,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: { color: t.text, padding: 12, usePointStyle: true, pointStyle: 'circle' },
            },
            tooltip: {
              backgroundColor: t.tooltipBg,
              callbacks: {
                label: (ctx) => ` ${ctx.label}: ${fmtEuro(ctx.raw)}`,
              },
            },
          },
        },
      });
    },

    renderBetaald(canvasId, data, t) {
      const el = document.getElementById(canvasId);
      if (!el) return;
      this.charts[canvasId] = new Chart(el, {
        type: 'doughnut',
        data: {
          labels: ['Betaald', 'Open'],
          datasets: [{
            data: [data.betaald, data.open],
            backgroundColor: ['#10b981', '#f59e0b'],
            borderWidth: 0,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: { color: t.text, padding: 12, usePointStyle: true, pointStyle: 'circle' },
            },
            tooltip: {
              backgroundColor: t.tooltipBg,
              callbacks: {
                label: (ctx) => ` ${ctx.label}: ${fmtEuro(ctx.raw)}`,
              },
            },
          },
        },
      });
    },

    renderMaand(canvasId, data, t) {
      const el = document.getElementById(canvasId);
      if (!el || !data?.length) return;
      const labels = data.map((d) => {
        const [y, m] = d.maand.split('-');
        return `${m}/${y.slice(2)}`;
      });
      this.charts[canvasId] = new Chart(el, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Losse kosten',
              data: data.map((d) => d.losse / 100),
              backgroundColor: '#2563eb',
              borderRadius: 4,
              stack: 'costs',
            },
            {
              label: 'Dossierkosten',
              data: data.map((d) => d.dossier / 100),
              backgroundColor: '#7c3aed',
              borderRadius: 4,
              stack: 'costs',
            },
            {
              label: 'Cumulatief actueel',
              type: 'line',
              data: data.map((d) => d.cumulatief_actueel !== null ? d.cumulatief_actueel / 100 : null),
              borderColor: '#10b981',
              backgroundColor: '#10b981',
              borderWidth: 3,
              tension: 0.1,
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: false,
            },
            {
              label: 'Cumulatief geprojecteerd',
              type: 'line',
              data: data.map((d) => d.cumulatief_projectie !== null ? d.cumulatief_projectie / 100 : null),
              borderColor: '#f59e0b',
              backgroundColor: '#f59e0b',
              borderWidth: 3,
              borderDash: [5, 5],
              tension: 0.1,
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: false,
            },
            {
              label: 'Budgetlimiet',
              type: 'line',
              data: data.map((d) => d.budget_limiet !== null ? d.budget_limiet / 100 : null),
              borderColor: '#ef4444',
              backgroundColor: '#ef4444',
              borderWidth: 2,
              borderDash: [3, 3],
              pointRadius: 0,
              pointHoverRadius: 0,
              fill: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          scales: {
            x: {
              stacked: true,
              ticks: { color: t.text },
              grid: { display: false },
            },
            y: {
              stacked: true,
              ticks: {
                color: t.text,
                callback: (v) => '\u20AC ' + v.toLocaleString('nl-BE'),
              },
              grid: { color: t.grid },
            },
          },
          plugins: {
            legend: {
              labels: { color: t.text, usePointStyle: true, pointStyle: 'circle' },
            },
            tooltip: {
              backgroundColor: t.tooltipBg,
              callbacks: {
                label: (ctx) => ` ${ctx.dataset.label}: \u20AC ${ctx.raw.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}`,
              },
            },
          },
        },
      });
    },

    renderBar(canvasId, data, t) {
      const el = document.getElementById(canvasId);
      if (!el || !data?.length) return;
      const top = data.slice(0, 15);
      this.charts[canvasId] = new Chart(el, {
        type: 'bar',
        data: {
          labels: top.map((d) => d.label),
          datasets: [{
            data: top.map((d) => d.total / 100),
            backgroundColor: '#0ea5e9',
            borderRadius: 4,
          }],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                color: t.text,
                callback: (v) => '\u20AC ' + v.toLocaleString('nl-BE'),
              },
              grid: { color: t.grid },
            },
            y: {
              ticks: { color: t.text },
              grid: { display: false },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: t.tooltipBg,
              callbacks: {
                label: (ctx) => ` \u20AC ${ctx.raw.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}`,
              },
            },
          },
        },
      });
    },
  };
}
